/**
 * Authentication API functions for user authentication and account management
 */
import { useAtom } from "jotai";
import getAPIEndpoint from "./APIEndpoint";
import { ClientStateAtom } from "@/store/ClientStateAtom";

/**
 * Changes the password for the currently logged in user
 * @param password - The new password
 * @returns Promise with the API response
 */
export const ChangePassword = async (password: string) => {
  try {
    const response = await fetch(getAPIEndpoint() + "/users/password", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ password }),
    });
    return await response.json();
  } catch (error) {
    console.error("API Call: Error changing password", error);
    return {
      success: false,
      title: "Server error",
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Validates the current authentication token
 * @returns Promise with the API response and user profile if successful
 */

export const checkToken = async () => {
  console.log("checkToken");
  try {
    const response = await fetch(getAPIEndpoint() + "/users/profile", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        authorization: `${localStorage.getItem("token")}`,
      },
    });

    const data = await response.json();

    return data;
  } catch (error) {
    return {
      success: false,
      title: "Server error",
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Authenticates a user with email and password
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise with the API response and auth token if successful
 */
export const login = async (email: string, password: string) => {
  try {
    const response = await fetch(getAPIEndpoint() + "/users/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const result = await response.json();

    if (result.success === true) {
      localStorage.setItem("token", result.token);
    }

    return result;
  } catch (error) {
    console.error("API Call: Error logging in", error);
    return {
      success: false,
      title: "Server error",
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Registers a new user account
 * @param email - User's email address
 * @param password - User's password
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param clientName - Client company name
 * @returns Promise with the API response and auth token if successful
 */
export const register = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  clientName: string
) => {
  try {
    const refId = localStorage.getItem("referringId");
    const response = await fetch(getAPIEndpoint() + "/users/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        refId,
        firstName,
        lastName,
        clientName,
      }),
    });

    const result = await response.json();

    if (result.success === true) {
      localStorage.setItem("token", result.token);
    }

    return result;
  } catch (error) {
    console.error("API Call: Error registering user", error);
    return {
      success: false,
      title: "Server error",
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Initiates a password reset for a user
 * @param email - User's email address
 * @returns Promise with the API response
 */
export const resetPassword = async (email: string) => {
  try {
    const response = await fetch(getAPIEndpoint() + "/users/reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    return await response.json();
  } catch (error) {
    console.error("API Call: Error resetting password", error);
    return {
      success: false,
      title: "Server error",
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
